import { Box, Button, MenuItem, Select, Typography } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import React, { PropsWithChildren, useCallback } from 'react';
import { useAppDispatch, useAppSelector } from '@store/hooks';
import {
  selectActiveCompany,
  selectActiveCompanyId,
  selectCompanies,
  selectPersonId,
} from '@features/workspace/store/selectors';
import { selectIsSupportUser } from '@store/features/auth';
import { setActiveCompanyId } from '@features/workspace/store';
import { truncate } from 'lodash';
import clsx from 'clsx';
import logger from '@app/services/logger';

const useStyles = makeStyles<any>()({
  buttonContainer: {
    backgroundColor: '#F6F6F7',
    width: '100%',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    paddingTop: '20px',
    paddingBottom: '20px',
  },

  loadingContent: {
    display: 'flex',
    flexDirection: 'column',
    flexGrow: 1,
    background: '#F5F6FA',
    borderRadius: '20px',
    marginTop: '-80px',
  },
  companySelectIcon: {
    width: '80px',
    display: 'flex',
    alignItems: 'center',
    color: '#61B8D8',
    fontSize: '12px',
    fontWeight: 700,
    lineHeight: '24px',
    transform: 'none',
    top: '15px',
  },
  companySelectIconOpen: {
    '& > i ': {
      transform: 'rotate(180deg)',
    },
  },
  switchText: {
    marginLeft: '4px',
  },
  iconWrapper: {
    display: 'flex',
    alignItems: 'center',
  },
});

export default function Layout({
  children,
}: PropsWithChildren<{}>): JSX.Element {
  const dispatch = useAppDispatch();

  const { classes } = useStyles(null);

  const personId = useAppSelector(selectPersonId);

  const isSupportUser = useAppSelector(selectIsSupportUser);

  const companies = useAppSelector(selectCompanies);

  const activeCompanyId = useAppSelector(selectActiveCompanyId);

  const activeCompany = useAppSelector(selectActiveCompany);

  const onActiveCompanyIdChange = useCallback(
    (companyId: string) => {
      dispatch(setActiveCompanyId(companyId));
    },
    [dispatch]
  );

  const onManageClick = () => {
    // Use target name to re-use same window tab
    const url = new URL(
      `/customer/company?personId=${personId}&activeCompanyId=${activeCompanyId}&isSupport=${isSupportUser}`,
      window.location.origin
    ).href;

    logger.debug('Open Manage Company URL:', url);
    window.open(url, 'LulaCustomerDetails');
  };

  const manageCompanyBtn = (
    <Box className={classes.buttonContainer}>
      <Button color="primary" variant="contained" onClick={onManageClick}>
        Manage company
      </Button>
    </Box>
  );

  return (
    <>
      <Box display="flex" justifyContent="center" p={2}>
        {companies.length > 1 && (
          <Select
            fullWidth
            classes={{
              icon: classes.companySelectIcon,
              iconOpen: classes.companySelectIconOpen,
            }}
            IconComponent={(props: { className: string }) => {
              return (
                <div className={clsx(classes.iconWrapper, props.className)}>
                  <span className={classes.switchText}>Switch</span>
                  <i
                    className="ri-arrow-down-s-line"
                    style={{ fontSize: '16px' }}
                  />
                </div>
              );
            }}
            sx={{
              borderRadius: '8px',
              border: '1px solid #EAEAEA',
              background: '#F5F6FA',
            }}
            value={activeCompanyId}
            onChange={(event) =>
              event.target.value && onActiveCompanyIdChange(event.target.value)
            }
          >
            {companies.map((company) => (
              <MenuItem
                key={`company-menu-item-${company.id}`}
                value={company.id}
              >
                {truncate(company.name, { length: 28 })}
              </MenuItem>
            ))}
          </Select>
        )}
        {companies.length === 1 && (
          <Typography variant="subtitle1">{activeCompany?.name}</Typography>
        )}
      </Box>
      {manageCompanyBtn}
      {children}
      {manageCompanyBtn}
    </>
  );
}
